import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyDwaveMode({ brand, today, Copy }: ModeRendererProps) {
  // Namespaced IDs to avoid collisions
  const idBase = React.useMemo(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      const param = url
        ? url.searchParams.get("id") || url.searchParams.get("uid") || url.searchParams.get("modeId") || url.searchParams.get("mode_id")
        : null;
      return param ? `dwave_${param}` : `dwave_${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      return `dwave_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);
  const gradId = `${idBase}_grad`;
  const glowId = `${idBase}_glow`;

  const defs = (
    <defs>
      <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#001F3F" />
        <stop offset="50%" stopColor="#6A0DAD" />
        <stop offset="100%" stopColor="#9370DB" />
      </linearGradient>
      <radialGradient id={glowId} cx="50%" cy="50%">
        <stop offset="0%" stopColor="#9370DB" stopOpacity={0.6} />
        <stop offset="100%" stopColor="#001F3F" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Annealing wells: energy landscape
  const annealingWells: React.ReactNode[] = [];
  for (let i = 0; i < 12; i++) {
    for (let j = 0; j < 16; j++) {
      const x = 80 + i * 60;
      const y = 120 + j * 55;
      const depth = Math.sin(i * 0.7) * Math.cos(j * 0.5) * 0.5 + 0.5;
      annealingWells.push(
          <circle
            key={`well-${i}-${j}`}
            cx={x}
            cy={y}
            r={12 + depth * 10}
            fill={`url(#${glowId})`}
            stroke={`url(#${gradId})`}
            strokeWidth={1.5}
            opacity={0.3 + depth * 0.4}
          />
      );
    }
  }

  // Probability meshes: superposition states
  const probabilityMeshes: React.ReactNode[] = [];
  for (let row = 0; row < 18; row++) {
    const y = 100 + row * 50;
    const points = [];
    for (let col = 0; col < 13; col++) {
      const x = 70 + col * 55;
      const wave = Math.sin(row * 0.3 + col * 0.4) * 20;
      points.push(`${x},${y + wave}`);
    }
    probabilityMeshes.push(
      <polyline
        key={`mesh-${row}`}
        points={points.join(" ")}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={1}
        opacity={0.3}
      />
    );
  }

  // Interference rings: quantum coherence
  const interferenceRings: React.ReactNode[] = [];
  for (let i = 0; i < 20; i++) {
    const radius = 30 + i * 20;
    interferenceRings.push(
      <circle
        key={`ring-${i}`}
        cx={397}
        cy={561.5}
        r={radius}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={i % 2 === 0 ? 2 : 1}
        opacity={0.2 + (i % 3) * 0.1}
      />
    );
  }

  // Qubit nodes: quantum processor
  const qubitNodes: React.ReactNode[] = [];
  const rings = [120, 200, 280];
  rings.forEach((radius, ringIndex) => {
    const nodeCount = 6 + ringIndex * 4;
    for (let i = 0; i < nodeCount; i++) {
      const angle = (i * Math.PI * 2) / nodeCount;
      const x = 397 + radius * Math.cos(angle);
      const y = 561.5 + radius * Math.sin(angle);
      qubitNodes.push(
        <g key={`qubit-${ringIndex}-${i}`}>
          <circle cx={x} cy={y} r={18} fill={`url(#${glowId})`} />
          <circle cx={x} cy={y} r={12} fill="none" stroke="#9370DB" strokeWidth={2} />
          <circle cx={x} cy={y} r={6} fill="#6A0DAD" />
        </g>
      );
    }
  });

  // Central quantum processor
  const quantumCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={80} fill={`url(#${glowId})`} />
      <circle cx={397} cy={561.5} r={60} fill="none" stroke="#9370DB" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={40} fill="none" stroke="#6A0DAD" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={20} fill="#9370DB" opacity={0.5} />
      <text
        x={397}
        y={568}
        fontFamily="monospace"
        fontSize={18}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        Q
      </text>
    </g>
  );

  return (
    <g>
      {defs}
      {probabilityMeshes}
      {annealingWells}
      {interferenceRings}
      {qubitNodes}
      {quantumCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">
          {Copy?.title}
        </text>
        <text x={0} y={22} fontSize={14}>
          {Copy?.subtitle}
        </text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>
          {Copy?.date}
        </text>
      </g>
    </g>
  );
}
